import React, { useEffect, useRef, useState } from 'react';
import {
  Search, ShoppingBag, LogOut, ChevronDown,
  Heart, User, Package, Settings, Crown, X, ArrowRight
} from 'lucide-react';
import gsap from 'gsap';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthModal from '../auth/AuthModal';
import CartDrawer from './CartDrawer';
import { BASE_URL, ENDPOINTS, getImageUrl, getCart } from '../../apiConfig';
import logo from '../../assets/logo.png';
import './Navbar.css';

const NAV_LINKS = ['HOME', 'SHOP', 'COLLECTIONS', 'ABOUT US', 'CONTACT'];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [returnPath, setReturnPath] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('HOME');

  const [cartCount, setCartCount] = useState(0);
  const [stockToast, setStockToast] = useState({ show: false, productName: '' });
  const [wishCount, setWishCount] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('wishlistItems') || '[]').length;
    } catch { return 0; }
  });

  const navRef = useRef(null);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchOverlayRef = useRef(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close menu and sync active tab on route change
  useEffect(() => {
    setIsMenuOpen(false);

    const path = location.pathname;
    if (path === '/') {
      setActiveLink('HOME');
    } else if (path.startsWith('/shop')) {
      setActiveLink('SHOP');
    } else if (path.startsWith('/collections')) {
      setActiveLink('COLLECTIONS');
    } else if (path.startsWith('/about')) {
      setActiveLink('ABOUT US');
    } else if (path.startsWith('/contact')) {
      setActiveLink('CONTACT');
    } else {
      setActiveLink(''); // Clear active highlight for sub-pages like profile, product details, etc.
    }
  }, [location.pathname]);

  // Handle body scroll lock when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Function to refresh counts from API and Storage
  const refreshCounts = async (e) => {
    try {
      console.log("Navbar refreshCounts triggered. Event detail:", e?.detail);
      if (e && e.detail && (e.detail.totalQty !== undefined || e.detail.totalItems !== undefined)) {
        const count = e.detail.totalQty || e.detail.totalItems || e.detail.items?.length || 0;
        console.log("Navbar setting count directly from event:", count);
        setCartCount(count);
      } else {
        const cartData = await getCart();
        const count = cartData.totalQty || cartData.totalItems || cartData.items?.length || 0;
        console.log("Navbar setting count from getCart API:", count);
        setCartCount(count);
      }

      const wish = JSON.parse(localStorage.getItem('wishlistItems') || '[]');
      setWishCount(wish.length);
    } catch (err) {
      console.error("Error refreshing counts", err);
    }
  };

  const handleCartUpdated = (e) => {
    refreshCounts(e);
    // Open cart drawer only when item is added to cart
    // Do NOT open when: update is from CartDrawer itself, or from successful checkout
    const source = e?.detail?.source;
    if (source !== 'CartDrawer' && source !== 'checkout') {
      setIsCartOpen(true);
    }
  };

  const refreshUser = () => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        setUser(JSON.parse(stored));
      } else {
        setUser(null);
      }
    } catch (e) {
      console.error("Error refreshing user", e);
    }
  };

  useEffect(() => {
    refreshCounts();
    refreshUser();

    const handleOutOfStockEvent = (e) => {
      const name = e?.detail?.productName || 'This product';
      setStockToast({ show: true, productName: name });
      setTimeout(() => {
        setStockToast(prev => ({ ...prev, show: false }));
      }, 3000);
    };

    const handleLoginRequired = (e) => {
      if (e?.detail?.returnTo) {
        setReturnPath(e.detail.returnTo);
      } else {
        setReturnPath(null);
      }
      setIsAuthModalOpen(true);
    };

    // Listen for custom events
    window.addEventListener('cartUpdated', handleCartUpdated);
    window.addEventListener('wishlistUpdated', refreshCounts);
    window.addEventListener('userUpdated', refreshUser);
    window.addEventListener('productOutOfStock', handleOutOfStockEvent);
    window.addEventListener('loginRequired', handleLoginRequired);

    // Also listen for storage events (for other tabs)
    window.addEventListener('storage', () => {
      refreshCounts();
      refreshUser();
    });

    const handleOpenSearch = () => setIsSearchOpen(true);
    window.addEventListener('openSearchOverlay', handleOpenSearch);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdated);
      window.removeEventListener('wishlistUpdated', refreshCounts);
      window.removeEventListener('userUpdated', refreshUser);
      window.removeEventListener('storage', refreshCounts);
      window.removeEventListener('openSearchOverlay', handleOpenSearch);
      window.removeEventListener('productOutOfStock', handleOutOfStockEvent);
      window.removeEventListener('loginRequired', handleLoginRequired);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSearchOpen) {
      fetchSearchData();
      // Delay focus slightly to allow for the sliding animation to start
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  const fetchSearchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      console.log("Navbar: Fetching data from", BASE_URL);
      const [catRes, prodRes] = await Promise.all([
        fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_CATEGORIES}`, { headers }),
        fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}`, { headers })
      ]);

      const cats = await catRes.json();
      const prods = await prodRes.json();

      console.log("Navbar: Data Received", { cats, prods });

      const catList = Array.isArray(cats.data) ? cats.data : (Array.isArray(cats) ? cats : []);
      setCategories(catList);

      const prodList = Array.isArray(prods) ? prods : (prods.data || []);
      setProducts(prodList);
    } catch (err) {
      console.error("Navbar: Fetch Error", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    console.log("Searching for:", searchQuery);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    const onResize = () => {
      if (window.innerWidth > 1120) setIsMenuOpen(false);
    };

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);

    const ctx = gsap.context(() => {
      gsap.fromTo(navRef.current,
        { y: -90, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.4, ease: 'expo.out', delay: 0.1 }
      );
    });

    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
      if (searchOverlayRef.current && !searchOverlayRef.current.contains(e.target) && isSearchOpen)
        setIsSearchOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', handleOutside);
      ctx.revert();
    };
  }, [isSearchOpen]);

  // Luxury Toggle & Mobile Menu Animation
  useEffect(() => {
    const ctx = gsap.context(() => {
      if (toggleRef.current) {
        const icon = toggleRef.current.querySelector('.toggle-icon');
        if (isMenuOpen) {
          gsap.to(icon, { rotate: 180, scale: 1.2, duration: 0.6, ease: "expo.out" });

          // Animate Mobile Menu Links
          gsap.fromTo(".mobile-nav-links li",
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.1,
              ease: "power4.out",
              delay: 0.3
            }
          );
        } else {
          gsap.to(icon, { rotate: 0, scale: 1, duration: 0.6, ease: "expo.out" });
        }
      }
    });
    return () => ctx.revert();
  }, [isMenuOpen]);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsAuthModalOpen(false);
    window.dispatchEvent(new Event('userUpdated'));
    if (returnPath) {
      navigate(returnPath);
      setReturnPath(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setShowDropdown(false);
    navigate('/');
    window.dispatchEvent(new Event('userUpdated'));
  };

  const handleNavClick = (link) => {
    setActiveLink(link);
    switch (link) {
      case 'HOME': navigate('/'); break;
      case 'SHOP': navigate('/shop'); break;
      case 'COLLECTIONS': navigate('/collections'); break;
      case 'ABOUT US': navigate('/about'); break;
      case 'CONTACT': navigate('/contact'); break;
      default: navigate('/');
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  ).slice(0, 5);

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} ref={navRef}>

        {/* ══ MOBILE TOGGLE ══ */}
        <button
          className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle Menu"
          ref={toggleRef}
        >
          <i className={`ri-menu-3-fill toggle-icon ${isMenuOpen ? 'ri-close-line' : ''}`}></i>
        </button>

        {/* ══ LOGO ══ */}
        <div className="nav-logo" onClick={() => navigate('/')}>
          <div className="logo-container">
            <div className="logo-ring"></div>
            <div className="logo-bottle">
              <img
                src={logo}
                alt="ESCENTRUM"
                className="logo-img"
              />
            </div>
          </div>
          <div className="logo-words">
            <span className="logo-brand">ESCENTRUM</span>
            <span className="logo-sub">EST. 2023 &nbsp;·&nbsp; LUXURY FRAGRANCES</span>
          </div>
        </div>

        {/* ══ DESKTOP LINKS ══ */}
        <ul className="nav-links desktop-only">
          {NAV_LINKS.map(link => (
            <li key={link}>
              <button
                className={`nav-link-btn ${activeLink === link ? 'active' : ''}`}
                onClick={() => handleNavClick(link)}
              >
                {link}
                <span className="link-dot" />
              </button>
            </li>
          ))}
        </ul>

        {/* ══ ACTIONS ══ */}
        <div className="nav-actions">
          <button
            className={`icon-btn search-icon ${isSearchOpen ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsSearchOpen(!isSearchOpen);
            }}
          >
            <Search size={17} />
          </button>

          <button
            className={`icon-btn wishlist-icon ${location.pathname === '/wishlist' ? 'active' : ''}`}
            onClick={() => {
              navigate('/wishlist');
            }}
            title="Wishlist"
          >
            <Heart size={17} />
            {user && wishCount > 0 && <span className="badge badge--pink">{wishCount}</span>}
          </button>

          <button
            className="icon-btn cart-icon"
            title="Cart"
            onClick={() => {
              setIsCartOpen(true);
            }}
          >
            <ShoppingBag size={17} />
            {user && cartCount > 0 && <span className="badge badge--gold">{cartCount}</span>}
          </button>

          <span className="sep" />

          {user ? (
            <div className="profile-wrap" ref={dropdownRef}>
              <button
                className={`profile-pill ${showDropdown ? 'profile-pill--open' : ''}`}
                onClick={() => setShowDropdown(s => !s)}
              >
                <div className="pill-avatar-wrap">
                  {user.profileImage ? (
                    <img src={user.profileImage} alt="Profile" className="pill-avatar-img" />
                  ) : (
                    <span className="pill-avatar">{user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}</span>
                  )}
                </div>
                <div className="pill-text">
                  <span className="pill-name">{user.fullName ? user.fullName.split(' ')[0] : 'User'}</span>
                  <span className="pill-role">Member</span>
                </div>
                <ChevronDown size={12} className={`pill-chevron ${showDropdown ? 'flip' : ''}`} />
              </button>

              {showDropdown && (
                <div className="dropdown">
                  <div className="drop-head">
                    {user.profileImage ? (
                      <img src={user.profileImage} alt="Profile" className="drop-avatar-img" />
                    ) : (
                      <div className="drop-avatar">{user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}</div>
                    )}
                    <div className="drop-meta">
                      <p className="drop-fullname">{user.fullName || 'User Name'}</p>
                      <p className="drop-email">{user.email || 'member@escentrum.com'}</p>
                    </div>
                    <Crown size={14} className="drop-crown" />
                  </div>
                  <div className="drop-divider" />
                  {[
                    { icon: <User size={14} />, label: 'My Profile', route: '/profile' },
                    { icon: <Package size={14} />, label: 'My Orders', route: '/profile/orders' },
                    { icon: <Heart size={14} />, label: 'Wishlist', route: '/wishlist' },
                    { icon: <Settings size={14} />, label: 'Settings', route: '/profile/settings' },
                  ].map(({ icon, label, route }) => (
                    <button key={label} className="drop-item" onClick={() => { setShowDropdown(false); navigate(route); }}>
                      <span className="drop-item-icon">{icon}</span>
                      <span>{label}</span>
                    </button>
                  ))}
                  <div className="drop-divider" />
                  <button className="drop-item drop-item--danger" onClick={handleLogout}>
                    <span className="drop-item-icon"><LogOut size={14} /></span>
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="signin-btn" onClick={() => setIsAuthModalOpen(true)}>
              <User size={15} />
              <span>Sign In</span>
            </button>
          )}
        </div>
      </nav>

      {/* ══ MOBILE MENU OVERLAY ══ */}
      <div className={`mobile-menu-overlay ${isMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-content" ref={menuRef}>
          <div className="mobile-menu-header">
            <span className="mobile-menu-title">NAVIGATION</span>
            <button className="mobile-menu-close" onClick={() => setIsMenuOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <ul className="mobile-nav-links">
            {NAV_LINKS.map((link, i) => (
              <li key={link}>
                <button
                  className={`mobile-nav-btn ${activeLink === link ? 'active' : ''}`}
                  onClick={() => {
                    handleNavClick(link);
                    setIsMenuOpen(false);
                  }}
                >
                  <span className="mobile-link-num">0{i + 1}</span>
                  <span className="mobile-link-text">{link}</span>
                  <ArrowRight size={16} className="mobile-link-arrow" />
                </button>
              </li>
            ))}
          </ul>
          <div className="mobile-menu-footer">
            <p className="mobile-footer-tag">ESCENTRUM LUXURY ARCHIVE</p>
            <div className="mobile-socials">
              <span className="mobile-social-item">IG</span>
              <span className="mobile-social-item">FB</span>
              <span className="mobile-social-item">TW</span>
            </div>
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
      />

      {/* ── SEARCH DRAWER (RIGHT SLIDING PANEL) ── */}
      <div className={`search-drawer-wrapper ${isSearchOpen ? 'open' : ''}`}>
        <div className="search-drawer-backdrop" onClick={() => setIsSearchOpen(false)} />
        <div className="search-drawer-card" ref={searchOverlayRef}>

          <div className="search-drawer-header">
            <div className="search-input-container">
              <Search size={20} className="s-input-icon" />
              <form onSubmit={handleSearchSubmit} className="s-input-form">
                <input
                  type="text"
                  placeholder="SEARCH ARCHIVES..."
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </form>
              <button className="s-drawer-close" onClick={() => setIsSearchOpen(false)}>
                <X size={22} />
              </button>
            </div>
          </div>

          <div className="search-drawer-body">
            <div className="search-section">
              <h3 className="search-section-title">CATEGORIES</h3>
              <div className="search-cat-grid">
                {categories.map(cat => (
                  <button
                    key={cat._id}
                    className="search-cat-item"
                    onClick={() => {
                      navigate(`/shop?category=${cat._id}`);
                      setIsSearchOpen(false);
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="search-section">
              <div className="search-section-header">
                <h3 className="search-section-title">SUGGESTIONS</h3>
                <span className="search-result-count">{filteredProducts.length} ITEMS</span>
              </div>

              <div className="search-results-list">
                {loading ? (
                  <div className="search-drawer-loading">
                    <div className="luxury-spinner"></div>
                  </div>
                ) : filteredProducts.length > 0 ? (
                  filteredProducts.map(prod => {
                    const price = prod.variants?.find(v => v.isDefault)?.price || prod.variants?.[0]?.price || 0;
                    return (
                      <div
                        key={prod._id}
                        className="search-result-item"
                        onClick={() => {
                          navigate(`/product/${prod._id}`);
                          setIsSearchOpen(false);
                        }}
                      >
                        <div className="s-result-thumb">
                          <img
                            src={getImageUrl(prod.images?.[0])}
                            alt={prod.name}
                          />
                        </div>
                        <div className="s-result-info">
                          <h4>{prod.name}</h4>
                          <p>{prod.brand || 'Luxury Fragrance'}</p>
                          <span className="s-result-price">Rs. {price.toLocaleString()}</span>
                        </div>
                        <ArrowRight size={16} className="s-result-arrow" />
                      </div>
                    );
                  })
                ) : (
                  <div className="search-drawer-empty">
                    <p>BEGIN TYPING TO EXPLORE ARCHIVES</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="search-drawer-footer">
            <button className="view-all-results" onClick={() => {
              navigate(`/shop?q=${searchQuery}`);
              setIsSearchOpen(false);
            }}>
              VIEW ALL COLLECTIONS
            </button>
          </div>
        </div>
      </div>

      {/* Luxury Out of Stock Toast Notification */}
      <div className={`luxury-stock-toast ${stockToast.show ? 'show' : ''}`}>
        <div className="luxury-stock-toast-icon">
          <Package size={18} strokeWidth={2} />
        </div>
        <div className="luxury-stock-toast-content">
          <span className="luxury-stock-toast-title">Out of Stock</span>
          <span className="luxury-stock-toast-desc">"{stockToast.productName}" is currently unavailable</span>
        </div>
      </div>

    </>
  );
};

export default Navbar;
